import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Flex, Input, Select, Text } from '@chakra-ui/react';
import { DateRangePicker } from 'react-date-range';
import { customStaticRanges } from 'utils/StaticData';
import useRegions from 'hooks/useRegions';
import { enGB } from 'date-fns/locale';
import { FaChevronDown } from 'react-icons/fa';
import { useDatePickerPosition } from 'hooks/useDatePickerPosition';

// Filter bar shared by the Sales Partner and Organization invoice lists.
// `searchLabel` switches the name search between "Seller" and "Organization".
const SellerInvoiceListFilter = ({
  searchLabel,
  name,
  setName,
  region,
  setRegion,
  setPage,
  setShowCalendar,
  showCalendar,
  tempDateRange,
  handleApply,
  handleClear,
  handleSelect,
  calendarFilterTitle,
  handleReset,
  exportSlot,
}) => {
  const { datePickerButtonRef, getCalendarPosition } = useDatePickerPosition();
  const { regions } = useRegions();

  return (
    <Flex
      alignItems={'flex-end'}
      flexWrap={'wrap'}
      mb={4}
      gap={{ base: '14px', md: '20px' }}
    >
      {/* Region Filter */}
      <Box width={{ base: '100%', md: 'auto' }}>
        <Text
          fontSize="xs"
          fontWeight="600"
          mb="1"
          color={region ? 'green.500' : 'gray.400'}
        >
          Region
        </Text>
        <Select
          value={region}
          onChange={(e) => {
            setPage(1);
            setRegion(e.target.value);
          }}
          width={{ base: '100%', md: '200px' }}
          background={'#fff'}
          borderRadius="10px"
        >
          <option value="">All</option>
          {regions?.map((regionItem) => (
            <option key={regionItem.id} value={regionItem.id}>
              {regionItem.label}
            </option>
          ))}
        </Select>
      </Box>

      {/* Name Filter (Seller / Organization) */}
      <Box width={{ base: '100%', md: 'auto' }}>
        <Text
          fontSize="xs"
          fontWeight="600"
          mb="1"
          color={name ? 'green.500' : 'gray.400'}
        >
          {searchLabel}
        </Text>
        <Input
          placeholder={`Search ${searchLabel}...`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          width={{ base: '100%', md: '200px' }}
          background={'#fff'}
          borderRadius="10px"
        />
      </Box>

      {/* Invoice Date Filter */}
      <Box width={{ base: '100%', md: 'auto' }}>
        <Text
          fontSize="xs"
          fontWeight="600"
          mb="1"
          color={
            calendarFilterTitle !== 'Select Date' ? 'green.500' : 'gray.400'
          }
        >
          Invoice Date
        </Text>
        <Box
          position="relative"
          flexShrink="0"
          width={{ base: '100%', md: '200px' }}
        >
          <Button
            ref={datePickerButtonRef}
            py="10px"
            px="15"
            fontWeight="semibold"
            type="button"
            bg="white"
            border="1px solid"
            borderColor="reviveGray"
            color="#292D32"
            borderRadius="8px"
            onClick={() => setShowCalendar(!showCalendar)}
            justifyContent="space-between"
            width={{ base: '100%', md: '200px' }}
          >
            {calendarFilterTitle}{' '}
            <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
          </Button>
          {/* Date Range Picker Dropdown */}
          {showCalendar && (
            <>
              <Box
                position="fixed"
                bg="rgb(0 0 0 / 40%)"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex="999"
                onClick={() => setShowCalendar(false)}
              ></Box>
              <Box
                p={{ base: '20px', md: '32px' }}
                mt="8px"
                position="fixed"
                {...getCalendarPosition()}
                zIndex="1000"
                bg="white"
                boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                borderRadius="20px"
                className="custom-date-picker"
                maxWidth={{ base: '95vw', md: '900px' }}
                width={{ base: '95vw', md: 'auto' }}
                maxHeight="90vh"
                overflowY="auto"
                overflowX="hidden"
              >
                <DateRangePicker
                  ranges={tempDateRange}
                  onChange={handleSelect}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={2}
                  direction="horizontal"
                  locale={enGB}
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                  staticRanges={customStaticRanges}
                  inputRanges={[]}
                />
                <Flex justifyContent="right" gap="12px" marginTop="30px">
                  <Button
                    className="clear-btn"
                    border="1px solid"
                    borderColor="reviveGray"
                    borderRadius="10px"
                    onClick={handleClear}
                    _hover={{ bg: 'gray.50' }}
                  >
                    Clear
                  </Button>
                  <Button
                    className="apply-btn"
                    bg="reviveOrange"
                    color="white"
                    borderRadius="10px"
                    _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                    onClick={handleApply}
                  >
                    Apply
                  </Button>
                </Flex>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Reset Button, plus whatever the tab wants beside it (RAT-1677: each tab's
          Export button, for the roles allowed to export) */}
      <Flex gap={{ base: '14px', md: '20px' }} alignItems="flex-end">
        <Button onClick={handleReset}>Reset</Button>
        {exportSlot}
      </Flex>
    </Flex>
  );
};

SellerInvoiceListFilter.propTypes = {
  searchLabel: PropTypes.string.isRequired,
  name: PropTypes.string,
  setName: PropTypes.func,
  region: PropTypes.string,
  setRegion: PropTypes.func,
  setPage: PropTypes.func,
  setShowCalendar: PropTypes.func,
  showCalendar: PropTypes.bool,
  tempDateRange: PropTypes.array,
  handleApply: PropTypes.func,
  handleClear: PropTypes.func,
  handleSelect: PropTypes.func,
  calendarFilterTitle: PropTypes.string,
  handleReset: PropTypes.func,
  exportSlot: PropTypes.node,
};

export default SellerInvoiceListFilter;
